import React, { useState, useMemo } from 'react';
import { SEO } from './SEO';
import '../styles/skills.css';

interface Skill {
  name: string;
  type: string;
  tags: string;
  description: string;
  effects: string[];
}

export const Skills: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 21;
  const skills = require('../data/all_skills.json') as Skill[];

  // Filter skills based on search term
  const filteredSkills = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return skills.filter(skill => 
      (skill.name?.toLowerCase() || '').includes(searchLower) ||
      (skill.type?.toLowerCase() || '').includes(searchLower) ||
      (skill.tags?.toLowerCase() || '').includes(searchLower) ||
      (skill.description?.toLowerCase() || '').includes(searchLower)
    );
  }, [skills, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSkills.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSkills = filteredSkills.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <SEO 
        title="POE2 Skills"
        description="Complete database of Path of Exile 2 skills. Find detailed information about skill gems, mechanics, and usage."
        canonicalUrl="https://www.poe2skills-ai.com/skills"
      />
      <div className="skills-container">
        <h1>PoE2 Skill Gems</h1>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search skills by name, type, or tags..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="search-input"
          />
        </div>

        <div className="skills-grid">
          {currentSkills.length > 0 ? (
            currentSkills.map((skill, index) => (
              <div key={`${skill.name}-${index}`} className="skill-card">
                <div className="skill-type">{skill.type}</div>
                <h2>{skill.name}</h2>
                <p className="skill-description">{skill.description}</p>
                <div className="skill-tags">
                  {skill.tags.split(', ').filter(tag => tag).map((tag, tagIndex) => (
                    <span key={`${tag}-${tagIndex}`} className="skill-tag">{tag}</span>
                  ))}
                </div>
                <div className="skill-meta">
                  {skill.effects.map((effect, effectIndex) => (
                    <div key={`${effect}-${effectIndex}`} className="skill-effects">
                      {effect}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              No skills found matching "{searchTerm}"
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button ${pageNum === currentPage ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};